/**
 * React hooks for WalletManager
 *
 * These hooks provide a React-friendly interface to the WalletManager class.
 * They handle state management, loading states, and error handling.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  WalletManager,
  SUPPORTED_CHAINS,
  type WalletState,
  type WalletManagerConfig,
  type ChainBalance,
  type MultiChainAddresses,
} from '../wallet';
import type { NetworkType } from '../types';

export interface UseWalletManagerOptions extends WalletManagerConfig {
  autoCheckWallet?: boolean;
}

export interface UseWalletManagerReturn {
  // State
  state: WalletState;
  isLoading: boolean;
  error: string | null;

  // Basic Actions
  createWallet: (password: string) => Promise<{ seed: string; address: string }>;
  importWallet: (seed: string, password: string) => Promise<void>;
  unlock: (password: string) => Promise<void>;
  lock: () => void;
  deleteWallet: () => Promise<void>;
  fetchBalance: () => Promise<string>;

  // Multi-chain Actions
  selectedChain: NetworkType;
  setSelectedChain: (chain: NetworkType) => void;
  chainBalances: ChainBalance[];
  fetchAllBalances: () => Promise<ChainBalance[]>;
  getAddressForChain: (chain: NetworkType) => string | null;
  getAllAddresses: () => Partial<MultiChainAddresses>;
  supportedChains: NetworkType[];

  // Utilities
  hasWallet: () => Promise<boolean>;
  getSeed: () => string | null;
  manager: WalletManager;
}

/**
 * React hook for managing a WDK wallet with multi-chain support
 */
export function useWalletManager(
  options: UseWalletManagerOptions = {}
): UseWalletManagerReturn {
  const { autoCheckWallet = true, ...config } = options;

  // Create manager instance (memoized)
  const manager = useMemo(() => new WalletManager(config), [
    config.network,
    config.rpcUrl,
  ]);

  // State
  const [state, setState] = useState<WalletState>({
    isInitialized: false,
    isLocked: true,
    address: null,
    balance: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedChain, setSelectedChainState] = useState<NetworkType>('ethereum');
  const [chainBalances, setChainBalances] = useState<ChainBalance[]>([]);

  // Update state from manager
  const updateState = useCallback(() => {
    setState(manager.getExtendedState());
  }, [manager]);

  // Check if wallet exists on mount
  useEffect(() => {
    if (autoCheckWallet) {
      manager.hasWallet().then((exists) => {
        if (exists) {
          setState((prev) => ({
            ...prev,
            isInitialized: true,
            isLocked: true,
          }));
        }
      });
    }
  }, [manager, autoCheckWallet]);

  // Create new wallet
  const createWallet = useCallback(
    async (password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        // Initialize storage for web
        await manager.initializeStorage(password);
        const result = await manager.createWallet(password);
        // Derive addresses for all chains using Tether WDK (async)
        // Falls back to sync derivation if WDK is not available
        try {
          await manager.deriveAllAddressesAsync();
        } catch {
          manager.deriveAllAddresses();
        }
        updateState();
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create wallet';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [manager, updateState]
  );

  // Import existing wallet
  const importWallet = useCallback(
    async (seed: string, password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await manager.initializeStorage(password);
        await manager.importWallet(seed, password);
        // Derive addresses for all chains using Tether WDK (async)
        try {
          await manager.deriveAllAddressesAsync();
        } catch {
          manager.deriveAllAddresses();
        }
        updateState();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import wallet';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [manager, updateState]
  );

  // Unlock wallet
  const unlock = useCallback(
    async (password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await manager.initializeStorage(password);
        await manager.unlock(password);
        // Derive addresses for all chains using Tether WDK (async)
        try {
          await manager.deriveAllAddressesAsync();
        } catch {
          manager.deriveAllAddresses();
        }
        updateState();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid password';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [manager, updateState]
  );

  // Lock wallet
  const lock = useCallback(() => {
    manager.lock();
    setChainBalances([]);
    updateState();
  }, [manager, updateState]);

  // Delete wallet
  const deleteWallet = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await manager.deleteWallet();
      setChainBalances([]);
      setState({
        isInitialized: false,
        isLocked: true,
        address: null,
        balance: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete wallet';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [manager]);

  // Fetch balance (for selected chain)
  const fetchBalance = useCallback(async () => {
    setIsLoading(true);
    // Don't clear error here - balance fetch errors shouldn't clear previous errors
    try {
      const balance = await manager.fetchBalance();
      setState((prev) => ({ ...prev, balance }));
      return balance;
    } catch (err) {
      // Log warning but don't throw - balance fetch errors shouldn't block UI
      console.warn('Failed to fetch balance:', err);
      // Return '0' instead of throwing to not block wallet operations
      setState((prev) => ({ ...prev, balance: '0' }));
      return '0';
    } finally {
      setIsLoading(false);
    }
  }, [manager]);

  // Fetch all chain balances
  const fetchAllBalances = useCallback(async () => {
    setIsLoading(true);
    // Don't clear error here - balance fetch errors shouldn't clear previous errors
    try {
      const balances = await manager.fetchAllBalances();
      setChainBalances(balances);
      return balances;
    } catch (err) {
      // Log warning but don't throw - balance fetch errors shouldn't block UI
      console.warn('Failed to fetch all balances:', err);
      // Return empty array instead of throwing
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [manager]);

  // Set selected chain
  const setSelectedChain = useCallback((chain: NetworkType) => {
    manager.setSelectedChain(chain);
    setSelectedChainState(chain);
  }, [manager]);

  // Get address for chain
  const getAddressForChain = useCallback(
    (chain: NetworkType) => manager.getAddressForChain(chain),
    [manager]
  );

  // Get all addresses
  const getAllAddresses = useCallback(
    () => manager.getAllAddresses(),
    [manager]
  );

  // Check if wallet exists
  const hasWallet = useCallback(() => manager.hasWallet(), [manager]);

  // Get seed (only if unlocked)
  const getSeed = useCallback(() => manager.getSeed(), [manager]);

  return {
    state,
    isLoading,
    error,
    createWallet,
    importWallet,
    unlock,
    lock,
    deleteWallet,
    fetchBalance,
    // Multi-chain
    selectedChain,
    setSelectedChain,
    chainBalances,
    fetchAllBalances,
    getAddressForChain,
    getAllAddresses,
    supportedChains: SUPPORTED_CHAINS,
    // Utilities
    hasWallet,
    getSeed,
    manager,
  };
}
